/* Dates, sizes and sender colors for webmail. */

const DAY = 86_400_000;

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** List dates: "4:18 PM" today, "Tue" this week, "Oct 3" this year, "Oct 3, 2025" before. */
export function listDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = startOfDay(now);
  const t = d.getTime();
  if (t >= today) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (t >= today - 6 * DAY) return d.toLocaleDateString("en-US", { weekday: "short" });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** The reading pane, and every hover: "Tue, Oct 14, 2026, 4:18 PM". */
export function fullDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** The list's headings: Today, Yesterday, This week, Earlier. */
export function dateGroup(iso: string, now = new Date()): string {
  const t = new Date(iso).getTime();
  const today = startOfDay(now);
  if (t >= today) return "Today";
  if (t >= today - DAY) return "Yesterday";
  if (t >= today - 6 * DAY) return "This week";
  return "Earlier";
}

export function fileSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(n < 10 * 1048576 ? 1 : 0).replace(/\.0$/, "")} MB`;
}

let crcTable: number[] | null = null;
function crc32(s: string): number {
  if (!crcTable) {
    crcTable = [];
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable.push(c >>> 0);
    }
  }
  const bytes = new TextEncoder().encode(s);
  let crc = 0xffffffff;
  for (const b of bytes) crc = crcTable[(crc ^ b) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** A color and an initial from the address, the same as the old webmail. Never a third-party avatar. */
export function avatarFor(seed: string, name?: string | null): { hue: number; initial: string } {
  const s = seed.trim().toLowerCase();
  const source = (name?.trim() || s.split("@")[0] || "?").replace(/^["'(<]+/, "");
  const ch = [...source][0]?.toUpperCase() ?? "?";
  return { hue: crc32(s || "?") % 360, initial: /[\p{L}\p{N}]/u.test(ch) ? ch : "?" };
}

export const displayName = (a: { name: string | null; address: string } | null | undefined) => (a ? a.name || a.address.split("@")[0] || a.address : "Unknown sender");

export function plural(n: number, one: string, many = `${one}s`) {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}
